/**
 * Clean uninstall: remove skills, MCP configs, and optionally credentials.
 */

import { existsSync, rmSync } from "fs";
import { homedir } from "os";
import { join } from "path";
import { deleteCredentials } from "../auth/credentials.js";
import { detectAgents } from "../agents/detector.js";
import { getAgent } from "../agents/index.js";
import { SKILL_DIR_NAME, CROSS_CLIENT_SKILLS_DIR } from "../constants.js";

interface UninstallOpts {
  keepCredentials?: boolean;
  yes?: boolean;
}

export async function runUninstall(opts: UninstallOpts): Promise<void> {
  console.log("\n🐟 Sailfish Autofix Uninstall\n");

  const detected = detectAgents();
  const installed = detected.filter((d) => d.hasSkill || d.hasMcp);

  // Check for orphaned cross-client skill that no detected agent owns
  const crossClientSkillPath = join(homedir(), CROSS_CLIENT_SKILLS_DIR, SKILL_DIR_NAME);
  const hasOrphanedSkill = existsSync(crossClientSkillPath) && installed.length === 0;

  if (installed.length === 0 && !hasOrphanedSkill && opts.keepCredentials) {
    console.log("  Nothing to uninstall.\n");
    return;
  }

  console.log("  Will remove:");
  for (const agent of installed) {
    const parts: string[] = [];
    if (agent.hasSkill) parts.push("skill");
    if (agent.hasMcp) parts.push("MCP config");
    console.log(`    - ${agent.name}: ${parts.join(" + ")}`);
  }
  if (hasOrphanedSkill) {
    console.log(`    - Orphaned cross-client skill (~/.agents/skills/${SKILL_DIR_NAME})`);
  }
  if (!opts.keepCredentials) {
    console.log("    - Sailfish credentials (~/.sailfish/credentials.json)");
  }
  console.log();

  // Confirmation
  if (!opts.yes) {
    const { createInterface } = await import("readline");
    const rl = createInterface({ input: process.stdin, output: process.stdout });
    const answer = await new Promise<string>((resolve) => {
      rl.question("  Proceed? [Y/n] ", resolve);
    });
    rl.close();
    if (answer.toLowerCase() === "n") {
      console.log("  Cancelled.\n");
      return;
    }
  }

  // Remove agent configs
  for (const agent of installed) {
    const writer = getAgent(agent.id);
    if (!writer) continue;

    try {
      if (agent.hasSkill) {
        writer.uninstallSkill();
        console.log(`  ✓ ${agent.name} — skill removed`);
      }
    } catch (err: any) {
      console.error(`  ✗ ${agent.name} — skill removal failed: ${err.message}`);
    }

    try {
      if (agent.hasMcp) {
        writer.uninstallMcp();
        console.log(`  ✓ ${agent.name} — MCP config removed`);
      }
    } catch (err: any) {
      console.error(`  ✗ ${agent.name} — MCP removal failed: ${err.message}`);
    }
  }

  // Clean up orphaned cross-client skill
  if (hasOrphanedSkill) {
    try {
      rmSync(crossClientSkillPath, { recursive: true });
      console.log("  ✓ Orphaned cross-client skill removed");
    } catch (err: any) {
      console.error(`  ✗ Orphaned skill removal failed: ${err.message}`);
    }
  }

  // Remove credentials
  if (!opts.keepCredentials) {
    try {
      deleteCredentials();
      console.log("  ✓ Credentials removed");
    } catch (err: any) {
      console.error(`  ✗ Credential removal failed: ${err.message}`);
    }
  }

  console.log("\n✅ Uninstall complete.\n");
}
